import {
  AccountBalanceWallet,
  AccessTime,
  CalendarMonth,
  Favorite,
  FitnessCenter,
  LocationOn,
  Person,
  TrendingUp,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { features } from '../../config/features'
import { useAuth } from '../../providers/auth'
import { GetTimeSpan } from '../../../hooks/timestamp'
import { useWorkouts, Workout } from '../../../providers/workouts'
import { useWorkoutSettings } from '../../../providers/workout-settings'
import Loading from '../../views/loading'

type CountItem = {
  label: string
  count: number
}

type StatTileProps = {
  icon: JSX.Element
  label: string
  value: string | number
}

const monthFormatter = new Intl.DateTimeFormat('sv-SE', {
  month: 'short',
})

const dateFormatter = new Intl.DateTimeFormat('sv-SE', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

function minutesBetween(start: Date, end: Date) {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
}

function startOfWeek(date: Date) {
  const result = new Date(date)
  const day = result.getDay()
  const diff = day === 0 ? -6 : 1 - day
  result.setDate(result.getDate() + diff)
  result.setHours(0, 0, 0, 0)
  return result
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function startOfDay(date: Date) {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

function countTop(labels: string[]): CountItem | null {
  const counts = labels.reduce<Record<string, number>>((acc, label) => {
    if (!label) return acc

    acc[label] = (acc[label] ?? 0) + 1
    return acc
  }, {})

  const [label, count] =
    Object.entries(counts).sort((a, b) => b[1] - a[1])[0] ?? []

  return label ? { label, count } : null
}

function getUserParticipant(workout: Workout, uid: string) {
  return workout.participantsList.find((participant) => participant.uid === uid)
}

function getChargedCredits(workout: Workout, uid: string) {
  const participant = getUserParticipant(workout, uid)
  return Number(participant?.creditsCharged ?? workout.credits ?? 0)
}

function formatWorkoutDate(workout: Workout) {
  return `${dateFormatter.format(workout.startTime.toDate())} ${GetTimeSpan(
    workout.startTime,
    workout.endTime
  )}`
}

function StatTile({ icon, label, value }: StatTileProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        height: '100%',
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    </Paper>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

export default function AccountStats() {
  const { user } = useAuth()
  const { workouts, loading, error } = useWorkouts()
  const { getWorkoutTypeName } = useWorkoutSettings()

  if (loading) return <Loading />
  if (!user) return null

  const now = new Date()
  const today = startOfDay(now)
  const weekStart = startOfWeek(now)
  const monthStart = startOfMonth(now)
  const ninetyDaysStart = new Date(today)
  ninetyDaysStart.setDate(ninetyDaysStart.getDate() - 90)

  const bookedWorkouts = workouts
    .filter((workout) => !!getUserParticipant(workout, user.uid))
    .sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis())

  const upcoming = bookedWorkouts.filter(
    (workout) => workout.startTime.toDate() >= now
  )
  const completed = bookedWorkouts.filter(
    (workout) => workout.endTime.toDate() < now
  )
  const completedThisWeek = completed.filter(
    (workout) => workout.startTime.toDate() >= weekStart
  )
  const completedThisMonth = completed.filter(
    (workout) => workout.startTime.toDate() >= monthStart
  )
  const completedLast90Days = completed.filter(
    (workout) => workout.startTime.toDate() >= ninetyDaysStart
  )
  const totalMinutes = completed.reduce(
    (sum, workout) =>
      sum +
      minutesBetween(workout.startTime.toDate(), workout.endTime.toDate()),
    0
  )
  const favoriteType = countTop(
    completed.map((workout) => getWorkoutTypeName(workout.type))
  )
  const favoriteLocation = countTop(completed.map((workout) => workout.location))
  const favoriteCoach = countTop(
    completed.flatMap((workout) =>
      workout.coaches.map((coach) => coach.displayName)
    )
  )
  const spentCredits = completed.reduce(
    (sum, workout) => sum + getChargedCredits(workout, user.uid),
    0
  )
  const reservedCredits = upcoming.reduce(
    (sum, workout) => sum + getChargedCredits(workout, user.uid),
    0
  )

  const monthlyTrend = Array.from({ length: 6 }, (_, index) => {
    const month = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1)
    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1)
    const count = completed.filter((workout) => {
      const start = workout.startTime.toDate()
      return start >= month && start < nextMonth
    }).length

    return {
      label: monthFormatter.format(month),
      count,
    }
  })
  const maxTrendCount = Math.max(...monthlyTrend.map((item) => item.count), 1)

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <StatTile
            icon={<CalendarMonth />}
            value={upcoming.length}
            label="Kommande bokningar"
          />
        </Grid>
        <Grid item xs={6}>
          <StatTile
            icon={<FitnessCenter />}
            value={completed.length}
            label="Genomförda pass"
          />
        </Grid>
        <Grid item xs={6}>
          <StatTile
            icon={<AccessTime />}
            value={`${Math.round(totalMinutes / 60)} h`}
            label="Total träningstid"
          />
        </Grid>
        <Grid item xs={6}>
          <StatTile
            icon={<TrendingUp />}
            value={completedThisMonth.length}
            label="Pass denna månad"
          />
        </Grid>
      </Grid>

      {features.credits && (
        <Section title="Credits">
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <StatTile
                icon={<AccountBalanceWallet />}
                value={user.credits ?? 0}
                label="Saldo"
              />
            </Grid>
            <Grid item xs={4}>
              <StatTile
                icon={<AccountBalanceWallet />}
                value={reservedCredits}
                label="Bundna"
              />
            </Grid>
            <Grid item xs={4}>
              <StatTile
                icon={<AccountBalanceWallet />}
                value={spentCredits}
                label="Spenderade"
              />
            </Grid>
          </Grid>
        </Section>
      )}

      <Section title="Perioder">
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <StatTile
              icon={<TrendingUp />}
              value={completedThisWeek.length}
              label="Denna vecka"
            />
          </Grid>
          <Grid item xs={4}>
            <StatTile
              icon={<TrendingUp />}
              value={completedThisMonth.length}
              label="Denna månad"
            />
          </Grid>
          <Grid item xs={4}>
            <StatTile
              icon={<TrendingUp />}
              value={completedLast90Days.length}
              label="Senaste 90 dagar"
            />
          </Grid>
        </Grid>
      </Section>

      <Section title="Favoriter">
        <Paper variant="outlined" sx={{ borderRadius: 1 }}>
          <List disablePadding>
            <ListItem>
              <ListItemIcon>
                <Favorite />
              </ListItemIcon>
              <ListItemText
                primary={favoriteType?.label ?? 'Ingen data'}
                secondary={
                  favoriteType
                    ? `${favoriteType.count} bokade pass`
                    : 'Mest bokade passtyp'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LocationOn />
              </ListItemIcon>
              <ListItemText
                primary={favoriteLocation?.label ?? 'Ingen data'}
                secondary={
                  favoriteLocation
                    ? `${favoriteLocation.count} bokade pass`
                    : 'Mest använda plats'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText
                primary={favoriteCoach?.label ?? 'Ingen data'}
                secondary={
                  favoriteCoach
                    ? `${favoriteCoach.count} bokade pass`
                    : 'Vanligaste tränare'
                }
              />
            </ListItem>
          </List>
        </Paper>
      </Section>

      <Section title="Trend">
        <Paper variant="outlined" sx={{ borderRadius: 1, p: 1.5 }}>
          <Stack spacing={1.5}>
            {monthlyTrend.map((item) => (
              <Box key={item.label}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.count}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(item.count / maxTrendCount) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Section>

      <Section title="Kommande">
        {upcoming.length > 0 ? (
          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <List disablePadding>
              {upcoming.slice(0, 5).map((workout) => (
                <ListItem key={workout.id}>
                  <ListItemIcon>
                    <CalendarMonth />
                  </ListItemIcon>
                  <ListItemText
                    primary={getWorkoutTypeName(workout.type)}
                    secondary={`${formatWorkoutDate(workout)} · ${workout.location}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Alert severity="info">Du har inga kommande bokningar.</Alert>
        )}
      </Section>
    </Box>
  )
}
